import { generateQueryString } from "@/utils/helpers";
import { baseApi } from "../baseApi";
import type { Dictionary } from "@/types";

export const businessTypesApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getBusinessTypeList: builder.query<Dictionary, Record<string, any>>({
      query: (params) => ({
        url: `/business-types${generateQueryString(params)}`,
        method: "GET",
      }),
      providesTags: ["Business-Types"],
      transformResponse: (response: any) => response.data,
    }),

    createBusinessType: builder.mutation({
      query: (data) => {
        const isFormData = data instanceof FormData;
        return {
          url: `/business-types`,
          method: "POST",
          body: data,
          ...(isFormData && {
            formData: true,
          }),
        };
      },
      invalidatesTags: ["Business-Types"],
    }),

    updateBusinessType: builder.mutation({
      query: (data) => {
        let id: string | number;
        let body: any;

        id = data.get("id") as string;
        data.delete("id");
        body = data;

        return {
          url: `/business-types/${id}`,
          method: "POST",
          body: body,
          formData: true,
        };
      },
      invalidatesTags: ["Business-Types"],
    }),

    publishAllBusinessTypes: builder.mutation({
      query: (data) => ({
        url: `/business-types/bulk-publish`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Business-Types"],
    }),

    unpublishAllBusinessTypes: builder.mutation({
      query: (data) => ({
        url: `/business-types/bulk-unpublish`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Business-Types"],
    }),

    deleteBusinessTypes: builder.mutation({
      query: (business_type_id) => ({
        url: `/business-types/${business_type_id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Business-Types"],
    }),

    bulkDeleteBusinessTypes: builder.mutation({
      query: (data) => ({
        url: `/business-types/bulk-delete`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Business-Types"],
    }),

    bulkUpdateBusinessTypes: builder.mutation({
      query: (data) => ({
        url: `/business-types/bulk-update`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: ["Business-Types"],
    }),

    createBusinessTypeSubcategory: builder.mutation({
      query: (data) => {
        const isFormData = data instanceof FormData;
        return {
          url: `/business-type-subcategories`,
          method: "POST",
          body: data,
          ...(isFormData && {
            formData: true,
          }),
        };
      },
      invalidatesTags: ["Business-Types"],
    }),

    updateBusinessTypeSubcategory: builder.mutation({
      query: (data) => {
        let id: string | number;
        let body: any;

        if (data instanceof FormData) {
          id = data.get("id") as string;
          data.delete("id");
          body = data;
        } else {
          id = data.id;
          body = { ...data };
          delete body.id;
        }

        return {
          url: `/business-type-subcategories/${id}`,
          method: "POST",
          body: body,
          ...(data instanceof FormData && {
            formData: true,
          }),
        };
      },
      invalidatesTags: ["Business-Types"],
    }),

    deleteBusinessTypeSubcategory: builder.mutation({
      query: (subcategory_id) => ({
        url: `/business-type-subcategories/${subcategory_id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Business-Types"],
    }),
  }),
});

export const {
  useUpdateBusinessTypeMutation,
  useGetBusinessTypeListQuery,
  useCreateBusinessTypeMutation,
  usePublishAllBusinessTypesMutation,
  useUnpublishAllBusinessTypesMutation,
  useDeleteBusinessTypesMutation,
  useBulkUpdateBusinessTypesMutation,
  useBulkDeleteBusinessTypesMutation,
  useCreateBusinessTypeSubcategoryMutation,
  useUpdateBusinessTypeSubcategoryMutation,
  useDeleteBusinessTypeSubcategoryMutation,
} = businessTypesApi;
